/* eslint react/prop-types: 0 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  CheckCircle2, Clock, Loader2, ShieldCheck, Download, FileSignature,
  AlertCircle,
} from 'lucide-react';

/**
 * Shared Execute-Agreement panel — used by:
 *   • SuperAdmin Data Room document drawer
 *   • External party (VdrPortal) document detail
 *   • Company-user (ClientDataRoomSection) document detail
 *
 * The component is mode-agnostic: parent passes the docEndpoint base + auth
 * headers and we drive the entire flow (Execute → poll → render downloads).
 *
 * Props:
 *   doc             — the VDR document object from the parent's GET response
 *   docEndpointBase — e.g. `${API}/admin/vdr/${cid}/documents/${did}`
 *   authHeaders     — headers object (Authorization / X-VDR-Token / etc.)
 *   onRefresh       — () => Promise<void>; parent should re-fetch & update doc
 *   testIdPrefix    — 'admin' | 'vdr' | 'client'
 *   canExecute      — bool; render Execute button only when true
 *   includeCertificateDownload — bool; show cert button alongside executed PDF
 */
export default function ExecutionPanel({
  doc, docEndpointBase, authHeaders,
  onRefresh, testIdPrefix, canExecute = true,
  includeCertificateDownload = true,
}) {
  const summary = doc?.approval_summary || { total: 0, approved: 0 };
  const total = summary.total || 0;
  const approved = summary.approved || 0;
  const execution = doc?.execution || { status: 'pending' };
  const status = execution.status || 'pending';
  const allApproved = total > 0 && approved === total;

  // Stage 1 — execute is enabled only when either ALL parties have signed
  // digitally OR an admin has flipped the doc to physical signing mode.
  const signingMode = execution.signing_mode || null;
  const allSigned = !!execution.all_signed;
  const signedCount = execution.signed_count || 0;
  const totalCount = execution.total_count || total;
  const isPhysical = signingMode === 'physical';
  const signaturesReady = isPhysical || allSigned;

  const [posting, setPosting] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const pollRef = useRef(null);

  // Auto-refresh while a background task is in flight.
  useEffect(() => {
    if (status === 'processing') {
      if (!pollRef.current) {
        pollRef.current = setInterval(() => { onRefresh?.(); }, 2500);
      }
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [status, onRefresh]);

  const handleExecute = async () => {
    setErrMsg('');
    setPosting(true);
    try {
      await axios.post(`${docEndpointBase}/execute`, {}, { headers: authHeaders });
      await onRefresh?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to start execution. Please retry.';
      setErrMsg(msg);
    } finally {
      setPosting(false);
    }
  };

  const handleDownloadExecuted = async () => {
    try {
      const r = await axios.get(
        `${docEndpointBase}/executed/download`,
        { params: { as_json: 1 }, headers: authHeaders },
      );
      if (r.data?.presigned_url) {
        window.open(r.data.presigned_url, '_blank', 'noopener');
      }
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || 'Could not generate download link.');
    }
  };

  // ── State 0: no reviewers configured ───────────────────────────────────────
  if (total === 0) return null;

  // ── State 4: agreement executed ────────────────────────────────────────────
  if (status === 'executed') {
    const cert = doc?.certificate;
    const approvers = execution.approvers_snapshot || [];
    return (
      <div
        className="mb-5 bg-[#059669]/10 border border-[#059669]/40 rounded-lg p-4"
        data-testid={`${testIdPrefix}-execution-executed`}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#059669]/25 border border-[#059669] flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-[#059669]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#059669]">Agreement executed</p>
            <p className="text-xs text-gray-300 mt-0.5">
              Executed on{' '}
              <span className="text-white">
                {fmtDateTime(execution.executed_at)}
              </span>
              {execution.executed_by?.name
                ? <> by <span className="text-white">{execution.executed_by.name}</span></>
                : null}
            </p>

            {approvers.length > 0 && (
              <div className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-gray-400">
                {approvers.map((a, i) => (
                  <div key={i} className="flex items-baseline gap-1.5">
                    <CheckCircle2 className="w-3 h-3 text-[#059669] shrink-0 translate-y-0.5" />
                    <span className="text-gray-200">{a.name}</span>
                    {a.role_label && <span className="text-gray-500">· {a.role_label}</span>}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleDownloadExecuted}
                className="inline-flex items-center gap-1.5 bg-[#059669] hover:bg-[#00d96e] text-black font-semibold rounded px-3 py-2 text-xs"
                data-testid={`${testIdPrefix}-download-executed-pdf`}
              >
                <Download className="w-3.5 h-3.5" /> Download Executed Agreement
              </button>
              {includeCertificateDownload && cert?.download_url && (
                <a
                  href={cert.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-gray-600 text-gray-200 rounded px-3 py-2 text-xs"
                  data-testid={`${testIdPrefix}-download-certificate`}
                >
                  <Download className="w-3.5 h-3.5" /> Download Certificate
                </a>
              )}
            </div>
            {cert?.audit_id && (
              <p className="mt-2 text-[11px] text-gray-500">
                Audit ID:{' '}
                <span className="font-mono text-gray-400">{cert.audit_id}</span>
              </p>
            )}
            {errMsg && (
              <p className="mt-2 text-xs text-red-400 inline-flex items-start gap-1">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {errMsg}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── State 3: execution in progress ─────────────────────────────────────────
  if (status === 'processing') {
    return (
      <div
        className="mb-5 bg-blue-500/10 border border-blue-400/40 rounded-lg p-4"
        data-testid={`${testIdPrefix}-execution-processing`}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-500/20 border border-blue-400/50 flex items-center justify-center shrink-0">
            <Loader2 className="w-5 h-5 text-blue-300 animate-spin" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-blue-200">Executing agreement…</p>
            <p className="text-xs text-gray-300 mt-1">
              Stamping every page of the document and generating your completion
              certificate. This usually completes in under 30 seconds.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── State 2: all approved, ready to execute ────────────────────────────────
  if (allApproved) {
    const disabledReason = !signaturesReady
      ? `Awaiting signatures: ${signedCount} of ${totalCount} signed`
      : null;
    const headline = isPhysical
      ? 'Marked for physical signing · ready to execute'
      : (allSigned
          ? 'All parties have signed · ready to execute'
          : 'Awaiting digital signatures');
    const subline = isPhysical
      ? 'Click below to stamp the document with the StartEase Agent execution seal and generate the completion certificate. No signature page will be appended. This action cannot be undone.'
      : (allSigned
          ? 'Every party has signed digitally. Anyone can now execute — this stamps the document, appends the signed signature page, and generates the completion certificate. This action cannot be undone.'
          : `Once every party has signed, anyone can execute. Currently ${signedCount} of ${totalCount} have signed.`);
    const canClick = canExecute && signaturesReady && !posting;

    return (
      <div
        className="mb-5 bg-[#059669]/10 border border-[#059669]/40 rounded-lg p-4"
        data-testid={`${testIdPrefix}-execution-ready`}
        data-signatures-ready={signaturesReady ? 'true' : 'false'}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#059669]/25 border border-[#059669] flex items-center justify-center shrink-0">
            <FileSignature className="w-5 h-5 text-[#059669]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#059669]">
              {headline}
            </p>
            <p className="text-xs text-gray-300 mt-1">
              {subline}
            </p>
            {canExecute ? (
              <button
                type="button"
                onClick={handleExecute}
                disabled={!canClick}
                title={disabledReason || ''}
                className="mt-3 inline-flex items-center gap-2 bg-[#059669] hover:bg-[#00d96e] disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-black font-semibold rounded px-4 py-2 text-sm"
                data-testid={`${testIdPrefix}-execute-btn`}
              >
                {posting && <Loader2 className="w-4 h-4 animate-spin" />}
                <ShieldCheck className="w-4 h-4" /> Execute Agreement
              </button>
            ) : (
              <p className="mt-2 text-xs text-gray-400 italic">
                An administrator or signatory will execute the agreement.
              </p>
            )}
            {disabledReason && canExecute && (
              <p
                className="mt-2 text-[11px] text-gray-400"
                data-testid={`${testIdPrefix}-execute-disabled-reason`}
              >
                {disabledReason}
              </p>
            )}
            {errMsg && (
              <p className="mt-2 text-xs text-red-400 inline-flex items-start gap-1"
                 data-testid={`${testIdPrefix}-execute-error`}>
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {errMsg}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── State 1: not yet fully approved ────────────────────────────────────────
  return (
    <div
      className="mb-5 bg-[#111827] border border-gray-700 rounded-lg p-3 flex items-center gap-3"
      data-testid={`${testIdPrefix}-execution-pending`}
    >
      <Clock className="w-4 h-4 text-amber-300 shrink-0" />
      <p className="text-xs text-gray-300">
        <span className="font-semibold text-white">{approved} of {total}</span>{' '}
        parties approved · execution unlocks once everyone has approved
      </p>
    </div>
  );
}

function fmtDateTime(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}
